import hoistNonReactStatics from 'hoist-non-react-statics';
import React, {
  FC,
  forwardRef,
  memo, useCallback, useContext,
  useMemo,
} from 'react';

import FormContext from 'ia-react-core/~/components/form/FormContext/FormContext';
import useResolveBehavior from 'ia-react-core/~/hooks/_useResolveBehavior/useResolveBehavior';
import useStoreSubscription from 'ia-react-core/~/hooks/_useStoreSubscription/useStoreSubscription';
import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import Behavior, { BehaviorInstance } from 'ia-react-core/~/services/behavior/_Behavior';
import { ReactReduxContext } from 'react-redux';
import getWrappedDisplayName from '../../utils/hocs/getWrappedDisplayName';

export type WithBehaviorsProps<Props extends object> = {
  [Prop in keyof Props]: Props[Prop] | BehaviorInstance<Props[Prop] extends boolean ? Props[Prop] : Props[Prop]>;
};

const withBehaviors = <
  BehaviorProps extends {}
>(WrappedComponent: FC<BehaviorProps>) => {
  const WithBehaviors = forwardRef((props: BehaviorProps & { validationMessages?: MessageMap; hideErrorMessages?: string[] }, ref) => {
    const { store } = useContext(ReactReduxContext);
    const { parentFormId } = useContext(FormContext);

    const resolve = useResolveBehavior('name' in props ? { defaultInput: props.name as string } : {});

    const getBehaviorProps = useCallback((state: unknown) => {
      const propNames = Object.keys(props) as (StringKeyOf<Omit<typeof props, 'validationMessages' | 'hideErrorMessages'>>)[];
      return (
        propNames.reduce<Partial<BehaviorProps & { validationMessages?: MessageMap; hideErrorMessages?: string[] }>>((result, propName) => {
          const behavior = props[propName] as Behavior;
          if (behavior instanceof Behavior) {
            if (behavior.callbacks.length) {
              result[propName] = ((event: Event) => {
                // Give some time to the form to update
                setTimeout(() => {
                  const latestState = store.getState();
                  if (resolve(behavior, latestState)) {
                    behavior.callbacks.forEach((callback) => {
                      callback({ event, state: latestState, dispatch: store.dispatch, parentFormId });
                    });
                  }
                });
              }) as any;
            } else {
              result[propName] = resolve(behavior, state);
            }

            if (behavior.validationMessage) {
              result.validationMessages = {
                ...props.validationMessages,
                ...result.validationMessages,
                [propName]: behavior.validationMessage,
              };
            } else if (behavior.validationMessage === false) {
              result.hideErrorMessages = [
                ...props.hideErrorMessages || [],
                ...result.hideErrorMessages || [],
                propName,
              ] as any;
            }
          }
          return result;
        }, {})
      );
    }, [parentFormId, props, resolve, store]);

    const behaviorProps = useStoreSubscription(getBehaviorProps);

    const MemoizedComponent = useMemo(() => memo(WrappedComponent), []);

    return (
      <MemoizedComponent
        ref={ref}
        {...props}
        {...behaviorProps}
      />
    );
  });

  WithBehaviors.displayName = getWrappedDisplayName('WithBehaviors', WrappedComponent);

  return hoistNonReactStatics(WithBehaviors, WrappedComponent);
};

export default withBehaviors;
